import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ArrowLeft,
  Loader2,
  Target,
  TrendingUp,
  FileText,
  Award,
} from "lucide-react";
import { OnboardingStep3Request } from "@/lib/api/auth";

interface OnboardingStep3Props {
  userType: "student" | "parent" | "teacher";
  onComplete: (data: OnboardingStep3Request) => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
}

const goalOptions = [
  {
    value: "assess_progress",
    label: "Assess progress",
    description: "Evaluate current oracy skills and areas for growth",
    icon: Target,
  },
  {
    value: "identify_strengths",
    label: "Identify strengths and areas for improvement",
    description: "Understand what your child does well and where to focus",
    icon: Award,
  },
  {
    value: "track_development",
    label: "Track development over time",
    description: "Monitor progress across multiple assessments",
    icon: TrendingUp,
  },
  {
    value: "prepare_assessments",
    label: "Prepare for formal assessments",
    description: "Build confidence and skills for upcoming evaluations",
    icon: FileText,
  },
];

export default function OnboardingStep3Goals({
  userType,
  onComplete,
  onBack,
  isLoading,
}: OnboardingStep3Props) {
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [dataUseConsent, setDataUseConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [parentalPermission, setParentalPermission] = useState(false);
  const [error, setError] = useState("");

  const toggleGoal = (goal: string) => {
    setSelectedGoals((prev) =>
      prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal],
    );
  };

  const handleSubmit = async () => {
    setError("");

    if (selectedGoals.length === 0) {
      setError("Please select at least one goal.");
      return;
    }

    if (!dataUseConsent || !termsConsent) {
      setError("Please accept the data use policy and terms & conditions.");
      return;
    }

    try {
      await onComplete({
        goals: selectedGoals,
        data_use_consent: dataUseConsent,
        terms_conditions_consent: termsConsent,
        parental_permission: parentalPermission,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const getGoalTitle = () => {
    switch (userType) {
      case "student":
        return "What are your goals?";
      case "parent":
        return "What are your goals?";
      case "teacher":
        return "What are your goals?";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Card className="w-full shadow-xl">
          <CardHeader className="space-y-1 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                3
              </span>
              <span className="text-sm text-muted-foreground">of 3</span>
            </div>
            <CardTitle className="text-2xl font-bold">
              {getGoalTitle()}
            </CardTitle>
            <CardDescription>
              Select all that apply to your use of Oracy
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950/20 rounded-lg">
                {error}
              </div>
            )}

            {/* Goals */}
            <div className="space-y-3">
              {goalOptions.map((goal) => {
                const Icon = goal.icon;
                const isSelected = selectedGoals.includes(goal.value);

                return (
                  <button
                    key={goal.value}
                    type="button"
                    onClick={() => toggleGoal(goal.value)}
                    disabled={isLoading}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`mt-0.5 ${isSelected ? "text-primary" : "text-muted-foreground"}`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium">{goal.label}</p>
                        <p className="text-sm text-muted-foreground">
                          {goal.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Consent Section */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium">Consent & Permissions</h4>

              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dataUseConsent}
                    onChange={(e) => setDataUseConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm">
                    {userType === "parent" ? (
                      <>
                        I consent to Oracy using my child's data to provide
                        personalised learning insights and improve the service.
                      </>
                    ) : (
                      <>
                        I consent to Oracy using my data to provide personalised
                        learning insights and improve the service.
                      </>
                    )}{" "}
                    <a
                      href="/privacy"
                      className="text-primary hover:underline"
                      target="_blank"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsConsent}
                    onChange={(e) => setTermsConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm">
                    I agree to the{" "}
                    <a
                      href="/terms"
                      className="text-primary hover:underline"
                      target="_blank"
                    >
                      Terms & Conditions
                    </a>
                  </span>
                </label>

              </div>
            </div>
          </CardContent>

          <CardFooter className="flex gap-4">
            <Button
              variant="outline"
              onClick={onBack}
              disabled={isLoading}
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                isLoading ||
                !dataUseConsent ||
                !termsConsent ||
                selectedGoals.length === 0
              }
              className="flex-1"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Completing...
                </>
              ) : (
                "Complete Setup"
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
